import type { Map as LibreMap, Marker } from 'maplibre-gl';
import mapLibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';
import type { ForecastSnapshot } from '../../adapters/forecast-http';
import type { WeatherSnapshot } from '../../adapters/weather-http';
import type { Bbox } from '../../consumer/fire-signals/contract';
import {
  basemap,
  MAP_PROVIDER,
  MAP_STUDY_AREA,
  mapFailure,
  mapStyleUrl,
  mapView,
  type MapFailure,
} from '../../consumer/geospatial/config';
import { createMapLoadAttempt, isLoadedProviderTile } from '../../consumer/geospatial/load-attempt';
import { selectedStationLabel, type GeoProjection } from '../../consumer/geospatial/projection';
import { installForecastLayer } from './forecast-layer';
import { installResearchLayers } from './research-layers';
import { installWeatherLayer } from './weather-layer';

const STUDY_PADDING = { top: 70, bottom: 105, left: 60, right: 60 };

const notices: Record<MapFailure, string> = {
  MAP_KEY_MISSING: '尚未配置地图服务：请在本地 frontend/.env.local 设置 VITE_MAPTILER_API_KEY。',
  MAP_ORIGIN_REJECTED: '地图服务来源受限，请检查 Key 与允许域名。',
  MAP_QUOTA_OR_RATE_LIMIT: '地图服务配额或速率受限，请稍后手动重试。',
  MAP_STYLE_UNAVAILABLE: '当前地图样式不可用，请切换底图或稍后重试。',
  MAP_OFFLINE: '地图服务连接失败，业务列表仍可使用。',
  WEBGL_UNAVAILABLE: '浏览器 WebGL 暂不可用，仍可从列表查看详情。',
};
interface Props {
  forecast?: ForecastSnapshot | null;
  weather?: WeatherSnapshot | null;
  weatherAreaId?: string;
  geo: GeoProjection;
  historical: boolean;
  loading: boolean;
  unavailable: boolean;
  selected: string;
  focusSelected?: string;
  areaRef?: string;
  studyBounds?: Bbox | null;
  search: string;
  select: (id: string) => void;
  navigate: (patch: Record<string, string | null>, replace?: boolean) => void;
  retry: () => void;
  maximized: boolean;
  maximize: () => void;
  collapsed: boolean;
  collapse: () => void;
}
export function RiskGisMap(props: Props) {
  const container = useRef<HTMLDivElement>(null),
    mapRef = useRef<LibreMap | null>(null);
  const markers = useRef<Marker[]>([]),
    latest = useRef(props);
  latest.current = props;
  const query = new URLSearchParams(props.search);
  const style = basemap(query.get('geoStyle') ?? import.meta.env.VITE_MAPTILER_DEFAULT_STYLE);
  const layerValue = query.get('geoLayers') ?? 'nasa,noaa,boundary';
  const layers = layerValue.split(',');
  const [failure, setFailure] = useState<MapFailure | null>(null),
    [ready, setReady] = useState(false);
  const [generation, setGeneration] = useState(0),
    [epoch, setEpoch] = useState(0);
  const [view, setView] = useState(''),
    [station, setStation] = useState('');
  const [instanceCount, setInstanceCount] = useState(0);
  const installRef = useRef<() => void>(() => {});
  const styleReadyRef = useRef(false);
  const attemptRef = useRef<{
    manager: ReturnType<typeof createMapLoadAttempt>;
    token: number;
    providerTileReceived: boolean;
  } | null>(null);
  const stationLabel = selectedStationLabel(props.geo, station, props.historical, layers);
  const key = String(import.meta.env.VITE_MAPTILER_API_KEY ?? '').trim();
  const keyValid = /^[A-Za-z0-9_-]{8,200}$/.test(key);
  useEffect(() => {
    if (!container.current || !keyValid) return;
    styleReadyRef.current = false;
    let disposed = false,
      map: LibreMap | null = null,
      observer: ResizeObserver | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const manager = createMapLoadAttempt((state) => {
      setFailure(state === 'MAP_LOADING' || state === 'MAP_BASEMAP_REAL' ? null : state);
      setReady(state === 'MAP_BASEMAP_REAL');
    });
    const attempt = { manager, token: manager.begin(), providerTileReceived: false };
    attemptRef.current = attempt;
    void import('maplibre-gl')
      .then((lib) => {
        if (disposed || !container.current) return;
        // MapLibre 6's default sibling worker URL is not preserved by Vite's
        // dependency prebundle/chunk names. Emit and resolve the real worker asset.
        lib.setWorkerUrl(mapLibreWorkerUrl);
        const initial = mapView(new URLSearchParams(latest.current.search).get('geoView'));
        try {
          map = new lib.Map({
            container: container.current,
            style: mapStyleUrl(
              basemap(
                new URLSearchParams(latest.current.search).get('geoStyle') ??
                  import.meta.env.VITE_MAPTILER_DEFAULT_STYLE,
              ),
              key,
            ),
            center: initial ? [initial[0], initial[1]] : MAP_STUDY_AREA.center,
            zoom: initial?.[2] ?? 6,
            bearing: initial?.[3] ?? 0,
            pitch: initial?.[4] ?? 0,
            maxZoom: 20,
            maxPitch: 60,
            attributionControl: { compact: false },
            transformRequest: (url) => {
              // Provider style resources are restricted to the configured HTTPS host.
              const parsed = new URL(url);
              if (
                parsed.protocol !== 'https:' ||
                !MAP_PROVIDER.allowedHosts.some((h) => h === parsed.hostname)
              )
                throw new Error('MAP_RESOURCE_ORIGIN_REJECTED');
              return { url };
            },
          });
        } catch {
          manager.fail(attempt.token, 'WEBGL_UNAVAILABLE');
          return;
        }
        mapRef.current = map;
        setInstanceCount(1);
        setEpoch((v) => v + 1);
        if (!initial && (!latest.current.areaRef || latest.current.studyBounds))
          map.fitBounds(
            latest.current.areaRef ? latest.current.studyBounds! : MAP_STUDY_AREA.bounds,
            { padding: STUDY_PADDING, duration: 0 },
          );
        const currentMap = map;
        currentMap.on('click', (event) => {
          if (latest.current.historical) return;
          // Station takes priority inside the forecast polygon; never query research layers.
          for (const id of ['vs11-live-weather', 'vs12-forecast-grid-hit']) {
            if (!currentMap.getLayer(id)) continue;
            const feature = currentMap.queryRenderedFeatures(event.point, { layers: [id] })[0];
            const selectionId = feature?.properties.selectionId;
            if (typeof selectionId === 'string') {
              latest.current.select(selectionId);
              break;
            }
          }
        });
        currentMap.on('error', (event) => {
          // Never render/log provider Error.message or URL: they may contain credentials.
          const error = event.error as Error & { status?: number };
          manager.fail(attempt.token, mapFailure(error.status ?? 0, !currentMap.isStyleLoaded()));
        });
        currentMap.on('style.load', () => {
          styleReadyRef.current = true;
          installRef.current();
        });
        currentMap.on('sourcedata', (event) => {
          if (isLoadedProviderTile(event)) attempt.providerTileReceived = true;
        });
        currentMap.on('idle', () => {
          manager.complete(
            attempt.token,
            currentMap.isStyleLoaded() === true,
            currentMap.areTilesLoaded(),
            attempt.providerTileReceived,
          );
        });
        currentMap.on('webglcontextlost', () => {
          manager.fail(attempt.token, 'WEBGL_UNAVAILABLE');
        });
        currentMap.on('moveend', () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            if (disposed) return;
            const center = currentMap.getCenter();
            const value = [
              center.lng,
              center.lat,
              currentMap.getZoom(),
              currentMap.getBearing(),
              currentMap.getPitch(),
            ]
              .map((n) => n.toFixed(6))
              .join(',');
            setView(value);
            if (new URLSearchParams(latest.current.search).get('geoView') !== value)
              latest.current.navigate({ geoView: value }, true);
          }, 300);
        });
        observer = new ResizeObserver(() => currentMap.resize());
        observer.observe(container.current);
      })
      .catch(() => {
        if (!disposed) manager.fail(attempt.token, 'WEBGL_UNAVAILABLE');
      });
    return () => {
      disposed = true;
      styleReadyRef.current = false;
      if (timer) clearTimeout(timer);
      manager.dispose();
      attemptRef.current = null;
      observer?.disconnect();
      markers.current.forEach((m) => m.remove());
      markers.current = [];
      map?.remove();
      mapRef.current = null;
    };
  }, [key, keyValid, generation]);
  const previousStyle = useRef(style);
  useEffect(() => {
    if (previousStyle.current === style) return;
    previousStyle.current = style;
    if (mapRef.current) {
      styleReadyRef.current = false;
      const attempt = attemptRef.current;
      if (attempt) {
        attempt.providerTileReceived = false;
        attempt.token = attempt.manager.begin();
      }
      try {
        mapRef.current.setStyle(mapStyleUrl(style, key), { diff: false });
      } catch {
        if (attempt) attempt.manager.fail(attempt.token, 'MAP_STYLE_UNAVAILABLE');
      }
    }
  }, [style, key]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    const install = () => {
      if (!styleReadyRef.current) return;
      const data = latest.current.geo;
      const enabled = (
        new URLSearchParams(latest.current.search).get('geoLayers') ?? 'nasa,noaa,boundary'
      ).split(',');
      installResearchLayers(map, data, latest.current.historical, enabled);
      installWeatherLayer(
        map,
        latest.current.historical ? null : (latest.current.weather ?? null),
        latest.current.weatherAreaId ?? '',
      );
      installForecastLayer(
        map,
        latest.current.historical ? null : (latest.current.forecast ?? null),
        latest.current.forecast?.sourceMode === 'DEMO' ? '' : (latest.current.weatherAreaId ?? ''),
      );
    };
    installRef.current = install;
    install();
    markers.current.forEach((m) => m.remove());
    markers.current = [];
    void import('maplibre-gl').then((lib) => {
      if (cancelled) return;
      for (const id of ['nasa', 'noaa'] as const) {
        if (!props.historical || !layers.includes(id)) continue;
        for (const feature of props.geo[id].features) {
          const [lon, lat] = feature.geometry.coordinates;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `risk-geo-marker risk-geo-${id}`;
          button.dataset.vs09Marker = id;
          button.dataset.id = feature.properties.id;
          button.dataset.longitude = String(lon);
          button.dataset.latitude = String(lat);
          button.dataset.rawRef = feature.properties.rawRef;
          button.dataset.timestamp = feature.properties.timestamp ?? 'METADATA_NO_OBSERVATION_TIME';
          button.dataset.sourceAuthority = feature.properties.sourceAuthority;
          button.setAttribute('aria-label', feature.properties.title);
          button.setAttribute('aria-pressed', String(props.selected === feature.properties.id));
          button.title = feature.properties.title;
          button.onclick = () =>
            id === 'nasa'
              ? latest.current.select(feature.properties.id)
              : setStation(feature.properties.id);
          const marker = new lib.Marker({ element: button }).setLngLat([lon!, lat!]).addTo(map);
          markers.current.push(marker);
        }
      }
    });
    return () => {
      cancelled = true;
    };
    // Layer membership is serialized; map creation increments epoch exactly once.
  }, [
    props.geo,
    props.historical,
    props.selected,
    props.weather,
    props.forecast,
    props.weatherAreaId,
    layerValue,
    epoch,
  ]);
  useEffect(() => {
    const map = mapRef.current,
      target = mapView(new URLSearchParams(props.search).get('geoView'));
    if (!map || !target) return;
    const c = map.getCenter();
    if (
      Math.abs(c.lng - target[0]) > 0.00001 ||
      Math.abs(c.lat - target[1]) > 0.00001 ||
      Math.abs(map.getZoom() - target[2]) > 0.00001 ||
      Math.abs(map.getBearing() - target[3]) > 0.00001 ||
      Math.abs(map.getPitch() - target[4]) > 0.00001
    )
      map.jumpTo({
        center: [target[0], target[1]],
        zoom: target[2],
        bearing: target[3],
        pitch: target[4],
      });
  }, [props.search]);
  const previousArea = useRef<string | null>(null);
  useEffect(() => {
    const area = props.areaRef ?? '';
    if (previousArea.current === area || !props.historical || !props.studyBounds || !mapRef.current)
      return;
    const firstDefaultView = previousArea.current === null && !area;
    previousArea.current = area;
    if (!firstDefaultView && !new URLSearchParams(props.search).has('geoView'))
      mapRef.current.fitBounds(props.studyBounds, { padding: STUDY_PADDING, duration: 300 });
  }, [props.areaRef, props.studyBounds, props.historical, props.search, epoch]);
  const previousSelection = useRef('');
  useEffect(() => {
    if (!props.focusSelected || previousSelection.current === props.focusSelected) return;
    const f = props.geo.nasa.features.find((f) => f.properties.id === props.focusSelected);
    if (f && mapRef.current) {
      previousSelection.current = props.focusSelected;
      mapRef.current.easeTo({
        center: [f.geometry.coordinates[0]!, f.geometry.coordinates[1]!],
        duration: 250,
      });
    }
  }, [props.focusSelected, props.geo, epoch]);
  const status = !keyValid
    ? 'MAP_KEY_MISSING'
    : (failure ?? (ready ? 'MAP_BASEMAP_REAL' : 'MAP_LOADING'));
  function reset() {
    props.navigate({ geoBbox: null, geoFrom: null, geoTo: null, geoView: null });
    mapRef.current?.fitBounds(props.studyBounds ?? MAP_STUDY_AREA.bounds, {
      padding: STUDY_PADDING,
      duration: 300,
    });
  }
  return (
    <div
      className="risk-gis-map"
      data-r2-map
      data-vs09-map
      data-status={status}
      data-style={MAP_PROVIDER.styles[style]}
      data-active-instance-count={instanceCount}
      data-view={view}
      data-visible-candidates={props.geo.nasa.features.length}
    >
      <div
        className="risk-gis-canvas"
        ref={container}
        role="region"
        aria-label="真实 GIS 地图：鼠标拖动、滚轮缩放；候选也可从右侧列表键盘访问"
      />
      <div className="risk-geo-toolbar">
        <label>
          底图{' '}
          <select
            aria-label="GIS 底图"
            value={style}
            onChange={(e) => props.navigate({ geoStyle: e.target.value })}
          >
            <option value="hybrid">卫星 Hybrid</option>
            <option value="satellite">卫星 Satellite</option>
            <option value="streets">街道 Streets</option>
          </select>
        </label>
        <details>
          <summary>图层与筛选</summary>
          <div className="risk-geo-options">
            {(['nasa', 'noaa', 'boundary'] as const).map((id, i) => (
              <label key={id}>
                <input
                  type="checkbox"
                  checked={layers.includes(id)}
                  disabled={!props.historical}
                  onChange={() =>
                    props.navigate({
                      geoLayers: layers.includes(id)
                        ? layers.filter((x) => x !== id).join(',') || 'none'
                        : [...layers, id].join(','),
                    })
                  }
                />
                {['NASA 历史热异常', 'NOAA 历史气象站', '研究选择范围（非行政界）'][i]}
              </label>
            ))}
            <label>
              起始 UTC
              <input
                aria-label="GIS 起始 UTC"
                type="datetime-local"
                value={(query.get('geoFrom') ?? '').slice(0, 16)}
                onChange={(e) =>
                  props.navigate({
                    geoFrom: e.target.value ? `${e.target.value}:00Z` : null,
                    selected: null,
                  })
                }
              />
            </label>
            <label>
              截止 UTC（不含）
              <input
                aria-label="GIS 截止 UTC"
                type="datetime-local"
                value={(query.get('geoTo') ?? '').slice(0, 16)}
                onChange={(e) =>
                  props.navigate({
                    geoTo: e.target.value ? `${e.target.value}:00Z` : null,
                    selected: null,
                  })
                }
              />
            </label>
            <button
              type="button"
              onClick={() =>
                props.navigate({
                  geoFrom: '2023-07-12T00:00:00Z',
                  geoTo: '2023-07-13T00:00:00Z',
                  selected: null,
                })
              }
            >
              2023-07-12 UTC
            </button>
            <button
              type="button"
              onClick={() => {
                const b = mapRef.current?.getBounds();
                if (b)
                  props.navigate({
                    geoBbox: [
                      Math.max(-180, b.getWest()),
                      Math.max(-90, b.getSouth()),
                      Math.min(180, b.getEast()),
                      Math.min(90, b.getNorth()),
                    ]
                      .map((n) => n.toFixed(6))
                      .join(','),
                    selected: null,
                  });
              }}
            >
              按当前地图范围筛选
            </button>
            <span data-vs09-bbox>{query.get('geoBbox') ?? '全部已加载研究范围'}</span>
            <button type="button" onClick={reset}>
              清除地图筛选
            </button>
          </div>
        </details>
      </div>
      <div className="risk-map-tools">
        <button type="button" aria-label="放大地图" onClick={() => mapRef.current?.zoomIn()}>
          ＋
        </button>
        <button type="button" aria-label="缩小地图" onClick={() => mapRef.current?.zoomOut()}>
          −
        </button>
        <button type="button" aria-label="重置地图视角" data-r2-reset onClick={reset}>
          ⌂
        </button>
        <button
          type="button"
          aria-label="定位北加州研究区"
          onClick={() =>
            mapRef.current?.fitBounds(MAP_STUDY_AREA.bounds, { padding: STUDY_PADDING })
          }
        >
          ◎
        </button>
        <button
          type="button"
          aria-label={props.maximized ? '还原地图' : '地图最大化'}
          data-r2-maximize
          aria-pressed={props.maximized}
          onClick={props.maximize}
        >
          ⛶
        </button>
        <button
          type="button"
          aria-label={props.collapsed ? '展开关注列表' : '折叠关注列表'}
          aria-pressed={props.collapsed}
          onClick={props.collapse}
        >
          ☷
        </button>
      </div>
      {status !== 'MAP_BASEMAP_REAL' && (
        <div className="risk-geo-status" role="status" data-vs09-provider-status={status}>
          <strong>{status}</strong>
          <span>
            {status === 'MAP_LOADING' ? '正在加载真实地图…' : notices[status as MapFailure]}
          </span>
          {status !== 'MAP_LOADING' && (
            <button type="button" onClick={() => setGeneration((v) => v + 1)}>
              重试地图
            </button>
          )}
        </div>
      )}
      <div
        className="risk-geo-scope"
        role="status"
        data-vs09-business-status={
          props.unavailable
            ? 'BUSINESS_API_UNAVAILABLE'
            : props.loading
              ? 'BUSINESS_LOADING'
              : !props.historical
                ? 'NO_COORDINATES'
                : !props.geo.scopeValid
                  ? 'INVALID_GEO_FILTER'
                  : props.geo.nasa.features.length
                    ? 'RESEARCH_NOT_VERIFIED'
                    : 'EMPTY_RESULT'
        }
      >
        {props.unavailable ? (
          <>
            <span>BUSINESS_API_UNAVAILABLE · 数据连接不可用；资料未知</span>
            <button type="button" onClick={props.retry}>
              重试资料
            </button>
          </>
        ) : props.loading ? (
          '正在读取历史/监测资料…'
        ) : !props.historical ? (
          'NO_COORDINATES · 合成监测来源未配置真实地理位置；关注列表仍可用'
        ) : !props.geo.scopeValid ? (
          'INVALID_GEO_FILTER · 请检查 UTC / bbox 筛选'
        ) : (
          `NASA / NOAA · 历史研究 · NOT_VERIFIED · 当前筛选 ${props.geo.nasa.features.length} 个候选${props.geo.nasa.features.length ? '' : ' · EMPTY_RESULT'} · 拒绝 ${props.geo.rejected} / 去重 ${props.geo.duplicates}`
        )}
        {stationLabel && (
          <button type="button" onClick={() => setStation('')}>
            {stationLabel} ×
          </button>
        )}
      </div>
    </div>
  );
}
